import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Star, CheckCircle, XCircle, RefreshCw, ArrowLeft } from 'lucide-react';
import Navbar from '../components/Navbar';
import AdminTabs from '../components/AdminTabs';
import AdminLoadingScreen from '../components/AdminLoadingScreen';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function StarDisplay({ rating }) {
  return (
    <div className="flex gap-0.5">
      {[1,2,3,4,5].map(s => (
        <Star key={s} className={`w-4 h-4 ${s <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'}`} />
      ))}
    </div>
  );
}

export default function AdminReviews() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [toast, setToast] = useState(null);

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/reviews/pending`, { credentials: 'include' });
      if (res.status === 401) { navigate('/login'); return; }
      if (res.status === 403) { navigate('/admin/dashboard'); return; }
      const data = await res.json();
      setReviews(Array.isArray(data) ? data : []);
    } catch {
      showToast('error', t('admin.errorLoadingReviews'));
    } finally {
      setLoading(false);
    }
  }, [navigate, t]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const handleAction = async (reviewId, action) => {
    setActionLoading(prev => ({ ...prev, [reviewId]: action }));
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/reviews/${reviewId}/${action}`, {
        method: 'POST', credentials: 'include'
      });
      if (res.ok) {
        showToast('success', action === 'approve' ? t('admin.reviewApproved') : t('admin.reviewRejected'));
        setReviews(prev => prev.filter(r => r.review_id !== reviewId));
      } else {
        showToast('error', t('admin.couldNotComplete'));
      }
    } catch {
      showToast('error', t('clientDashboard.connectionError'));
    } finally {
      setActionLoading(prev => ({ ...prev, [reviewId]: null }));
    }
  };

  const locale = i18n.language?.startsWith('en') ? 'en-US' : 'es-DO';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <AdminTabs />

      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-5 py-3 rounded-2xl shadow-lg text-white font-medium text-sm
          ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
          {toast.msg}
        </div>
      )}

      <div className="container mx-auto px-4 md:px-6 max-w-4xl py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <button onClick={() => navigate('/admin/dashboard')} className="mb-2 flex items-center gap-2 text-gray-500 hover:text-[#134168] text-sm font-medium transition-colors">
              <ArrowLeft className="w-4 h-4" /> {t('admin.back2Dashboard')}
            </button>
            <h1 className="text-3xl font-heading font-bold">{t('admin.reviewsModeration')}</h1>
            <p className="text-gray-500 text-sm mt-1">
              {loading ? '...' : t('admin.reviewsPendingCount', { count: reviews.length })}
            </p>
          </div>
          <button
            onClick={fetchReviews}
            disabled={loading}
            className="flex items-center gap-2 border border-gray-200 bg-white px-4 py-2 rounded-full text-sm font-medium hover:bg-gray-50 transition"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> {t('admin.refresh')}
          </button>
        </div>

        {loading ? (
          <AdminLoadingScreen chrome={false} className="py-20" message={t('admin.loadingReviews', 'Cargando reseñas...')} />
        ) : reviews.length === 0 ? (
          <div className="bg-white rounded-3xl p-14 shadow-sm text-center">
            <CheckCircle className="w-14 h-14 text-green-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">{t('admin.allUpToDate')}</h3>
            <p className="text-gray-500">{t('admin.noPendingReviews')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {reviews.map(review => (
              <div key={review.review_id} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <StarDisplay rating={review.rating} />
                      <span className="text-sm text-gray-500 font-mono">{review.booking_code}</span>
                    </div>
                    <p className="font-bold text-gray-900">{review.client_name || t('admin.rolesClient')}</p>
                    <p className="text-sm text-gray-500">{t('admin.aboutSomething')} <span className="font-medium">{review.asset_name}</span></p>
                  </div>
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold shrink-0">
                    {t('admin.statusPending')}
                  </span>
                </div>

                <blockquote className="bg-gray-50 rounded-xl p-4 text-gray-700 text-sm leading-relaxed mb-4 border-l-4 border-[#134168]">
                  "{review.comment}"
                </blockquote>

                <p className="text-xs text-gray-400 mb-4">
                  {review.created_at ? new Date(review.created_at).toLocaleString(locale) : ''}
                </p>

                <div className="flex gap-3">
                  <button
                    onClick={() => handleAction(review.review_id, 'approve')}
                    disabled={!!actionLoading[review.review_id]}
                    className="flex-1 flex items-center justify-center gap-2 bg-green-500 text-white py-2.5 rounded-full font-bold hover:bg-green-600 transition disabled:opacity-50"
                  >
                    <CheckCircle className="w-4 h-4" />
                    {actionLoading[review.review_id] === 'approve' ? t('admin.approving') : t('admin.approveAndPublish')}
                  </button>
                  <button
                    onClick={() => handleAction(review.review_id, 'reject')}
                    disabled={!!actionLoading[review.review_id]}
                    className="flex-1 flex items-center justify-center gap-2 border border-red-200 text-red-600 py-2.5 rounded-full font-bold hover:bg-red-50 transition disabled:opacity-50"
                  >
                    <XCircle className="w-4 h-4" />
                    {actionLoading[review.review_id] === 'reject' ? t('admin.rejecting') : t('admin.rejectShort')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
